import React, { useState, useEffect, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { doc, getDoc, updateDoc, setDoc, increment, collection, query, where, getDocs, onSnapshot, orderBy } from 'firebase/firestore';
import { limit } from 'firebase/firestore';
import { firestore, firebaseAuth } from '../firebase';
import { addToCart, removeFromCart } from '../Actions/cartActions';
import ProductImageGallery from '../Components/ProductImageGallery';
import Product from '../Components/Product';
import ProductRating from '../Components/ProductRating';
import ProductSpecificationTabs from '../Components/ProductSpecificationTabs';
import CouponDialog from '../Components/CouponDialog';
import ShareDialog from '../Components/ShareDialog';
import WishlistButton from '../Components/WishlistButton';
import './ProductScreen.css';
import { useToast } from '../Context/ToastContext';

const ProductScreen = () => {
  const { id: productId } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  
  // Mobile app redirect - check if on Android and redirect to app using Intent URL
  const [redirectAttempted, setRedirectAttempted] = useState(false);
  
  useEffect(() => {
    const isAndroid = /Android/i.test(navigator.userAgent);
    
    if (isAndroid && productId && !redirectAttempted) {
      setRedirectAttempted(true);
      
      // Android Intent URL - this is the ONLY way to open apps from Chrome on Android
      // Format: intent://HOST/PATH#Intent;scheme=SCHEME;package=PACKAGE;S.browser_fallback_url=URL;end
      const intentUrl = `intent://product?id=${productId}#Intent;scheme=shopiebazar;package=com.prakash.shopiebazar;S.browser_fallback_url=${encodeURIComponent('https://play.google.com/store/apps/details?id=com.prakash.shopiebazar')};end`;
      
      window.location.href = intentUrl;
    }
  }, [productId, redirectAttempted]);
  
  // State management
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [inStock, setInStock] = useState(false);
  const [productImages, setProductImages] = useState([]);
  const [specifications, setSpecifications] = useState([]);
  const [userRating, setUserRating] = useState(0);
  const [showCouponDialog, setShowCouponDialog] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [currentProductIndex, setCurrentProductIndex] = useState(-1);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  
  // Multiple suggestion types
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [searchBasedSuggestions, setSearchBasedSuggestions] = useState([]);
  const [trendingProducts, setTrendingProducts] = useState([]);
  const [categoryProducts, setCategoryProducts] = useState([]);
  
  // Refs for different suggestion sections
  const recentlyViewedRef = useRef(null);
  const searchBasedRef = useRef(null);
  const trendingRef = useRef(null);
  const categoryRef = useRef(null);
  const imagesContainerRef = useRef(null);
  const contentContainerRef = useRef(null);
  
  
  // Scroll states for different sections
  const [scrollStates, setScrollStates] = useState({
    related: { canScrollLeft: false, canScrollRight: false },
    recentlyViewed: { canScrollLeft: false, canScrollRight: false },
    searchBased: { canScrollLeft: false, canScrollRight: false },
    trending: { canScrollLeft: false, canScrollRight: false },
    category: { canScrollLeft: false, canScrollRight: false }
  });

  // Redux state
  const userSignin = useSelector(state => state.userSignin);
  const { userInfo } = userSignin;
  const signedInRole = (userInfo?.Role || userInfo?.role || '').toString().toLowerCase();
  const cart = useSelector(state => state.cart);
  const { cartItems } = cart;
  const suggestionsRef = useRef(null);
  const { success, error: showError } = useToast();

  // Check if product is already in cart or wishlist
  const isInCart = cartItems.some(item => item.product === productId);
  const currentUser = firebaseAuth.currentUser;

  useEffect(() => {
    if (productId) {
      // Reset user rating when product changes
      setUserRating(0);
      loadProductDetails();
      // Save current product to recently viewed
      if (currentUser) {
        saveToRecentlyViewed();
      }
    }
  }, [productId, currentUser]);

  // Always start at top and reset any scrollable containers when product changes
  useEffect(() => {
    try { window.scrollTo({ top: 0, left: 0, behavior: 'auto' }); } catch (_) {}
    try { document.documentElement.scrollTop = 0; document.body.scrollTop = 0; } catch (_) {}
    try { imagesContainerRef.current && imagesContainerRef.current.scrollTo && imagesContainerRef.current.scrollTo(0, 0); } catch (_) {}
    try { contentContainerRef.current && contentContainerRef.current.scrollTo && contentContainerRef.current.scrollTo(0, 0); } catch (_) {}
    try {
      const mainEl = document.querySelector('.product-screen');
      if (mainEl && typeof mainEl.scrollTop === 'number') mainEl.scrollTop = 0;
    } catch (_) {}
  }, [productId]);

  useEffect(() => {
    if (currentUser && productId) {
      loadUserRating();
    }
  }, [currentUser, productId]);

  useEffect(() => {
    const refs = [
      { ref: suggestionsRef, type: 'related' },
      { ref: recentlyViewedRef, type: 'recentlyViewed' },
      { ref: searchBasedRef, type: 'searchBased' },
      { ref: trendingRef, type: 'trending' },
      { ref: categoryRef, type: 'category' }
    ];

    const listeners = [];

    // Initialize scroll states after a delay to ensure DOM is ready
    const initializeScrollStates = () => {
      refs.forEach(({ ref, type }) => {
        const grid = ref.current;
        if (grid) {
          const handleScroll = () => checkScrollState(type);
          grid.addEventListener('scroll', handleScroll);
          listeners.push({ grid, handleScroll });
          
          // Initial check with longer delay
          setTimeout(() => checkScrollState(type), 500);
        }
      });
    };

    // Initialize after products are loaded
    setTimeout(initializeScrollStates, 100);

    return () => {
      listeners.forEach(({ grid, handleScroll }) => {
        grid.removeEventListener('scroll', handleScroll);
      });
    };
  }, [relatedProducts, recentlyViewed, searchBasedSuggestions, trendingProducts, categoryProducts]);

  const loadProductDetails = async () => {
    try {
      setLoading(true);
      
      // Get product document
      const productDoc = await getDoc(doc(firestore, 'PRODUCTS', productId));
      
      if (!productDoc.exists()) {
        setError('Product not found');
        setLoading(false);
        return;
      }

      const productData = productDoc.data();
      const pf = (productData.productFor || 'user').toString().toLowerCase();
      const allowed = (!signedInRole && pf === 'user') || (signedInRole === 'retailer' ? pf === 'retailer' : pf === 'user');
      if (!allowed) {
        setError('This product is not available for your account type.');
        setLoading(false);
        return;
      }
      setProduct({ id: productDoc.id, ...productData });

      // Load product images
      const images = [];
      for (let i = 1; i <= (productData.no_of_product_images || 1); i++) {
        if (productData[`product_image_${i}`]) {
          images.push(productData[`product_image_${i}`]);
        }
      }
      setProductImages(images);

      // Load specifications if using tab layout
      if (productData.use_tab_layout) {
        const specs = [];
        for (let i = 1; i <= (productData.total_spec_titles || 0); i++) {
          const specTitle = productData[`spec_title_${i}`];
          if (specTitle) {
            specs.push({
              type: 'title',
              title: specTitle
            });
            
            const totalFields = productData[`spec_title_${i}_total_fields`] || 0;
            for (let j = 1; j <= totalFields; j++) {
              const fieldName = productData[`spec_title_${i}_field_${j}_name`];
              const fieldValue = productData[`spec_title_${i}_field_${j}_value`];
              if (fieldName && fieldValue) {
                specs.push({
                  type: 'field',
                  name: fieldName,
                  value: fieldValue
                });
              }
            }
          }
        }
        setSpecifications(specs);
      }

      // Check stock availability
      try {
        const quantityQuery = query(
          collection(firestore, 'PRODUCTS', productId, 'QUANTITY'),
          orderBy('time', 'asc')
        );
        const quantitySnapshot = await getDocs(quantityQuery);
        const reservedQuantity = quantitySnapshot.size;
        const stockQuantity = productData.stock_quantity || 0;

        setInStock(reservedQuantity < stockQuantity);
      } catch (quantityError) {
        // If QUANTITY subcollection doesn't exist, assume no reservations
        console.error('Error checking stock availability:', quantityError);
        setInStock(true); // Assume in stock if we can't check
      }

      // Load related products for navigation
      await loadRelatedProducts(productData.category, productData.subcategory);
      
      // Load multiple suggestion types
      if (currentUser) {
        await loadRecentlyViewed();
        await loadSearchBasedSuggestions();
      }
      await loadTrendingProducts();
      await loadCategoryProducts(productData.category);
      
      setLoading(false);

    } catch (err) {
      console.error('Error loading product:', err);
      setError('Failed to load product details');
      setLoading(false);
    }
  };

  const checkScrollState = (sectionType = 'related') => {
    const refs = {
      related: suggestionsRef,
      recentlyViewed: recentlyViewedRef,
      searchBased: searchBasedRef,
      trending: trendingRef,
      category: categoryRef
    };
    
    const grid = refs[sectionType]?.current;
    if (!grid) return;
    
    const { scrollLeft, scrollWidth, clientWidth } = grid;
    const canScrollLeft = scrollLeft > 0;
    const canScrollRight = scrollLeft < scrollWidth - clientWidth - 1;
    
    setScrollStates(prev => ({
      ...prev,
      [sectionType]: { canScrollLeft, canScrollRight }
    }));
    
    // Legacy support for main suggestions
    if (sectionType === 'related') {
      setCanScrollLeft(canScrollLeft);
      setCanScrollRight(canScrollRight);
    }
  };

  const scrollSuggestions = (direction, sectionType = 'related') => {
    const refs = {
      related: suggestionsRef,
      recentlyViewed: recentlyViewedRef,
      searchBased: searchBasedRef,
      trending: trendingRef,
      category: categoryRef
    };
    
    const grid = refs[sectionType]?.current;
    if (!grid) return;
    
    const cardWidth = 180; // width of each product card
    const gap = 16; // gap between cards
    const scrollAmount = cardWidth + gap;
    const delta = direction === 'right' ? scrollAmount * 2 : -scrollAmount * 2;
    
    grid.scrollBy({ left: delta, behavior: 'smooth' });
    
    // Update scroll state after animation
    setTimeout(() => checkScrollState(sectionType), 300);
  };

  const loadRelatedProducts = async (category, subcategory) => {
    try {
      const productsRef = collection(firestore, 'PRODUCTS');
      let productsQuery;
      
      if (subcategory) {
        productsQuery = query(
          productsRef,
          where('subcategory', '==', subcategory),
          limit(20)
        );
      } else if (category) {
        productsQuery = query(
          productsRef,
          where('category', '==', category),
          limit(20)
        );
      } else {
        productsQuery = query(productsRef, limit(20));
      }

      const snapshot = await getDocs(productsQuery);
      const products = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })).filter(p => {
        const pf = (p.productFor || 'user').toString().toLowerCase();
        if (!signedInRole) return pf === 'user';
        return signedInRole === 'retailer' ? pf === 'retailer' : pf === 'user';
      });

      setRelatedProducts(products);
      
      // Find current product index
      const currentIndex = products.findIndex(p => p.id === productId);
      setCurrentProductIndex(currentIndex);
      
      // Check scroll state after products are loaded
      setTimeout(checkScrollState, 100);
      
    } catch (error) {
      console.error('Error loading related products:', error);
    }
  };

  const loadRecentlyViewed = async () => {
    try {
      if (!currentUser) return;
      
      const recentlyViewedRef = doc(firestore, 'USERS', currentUser.uid, 'USER_DATA', 'RECENTLY_VIEWED');
      const recentlyViewedDoc = await getDoc(recentlyViewedRef);
      
      if (recentlyViewedDoc.exists()) {
        const data = recentlyViewedDoc.data();
        const listSize = data.list_size || 0;
        const productIds = [];
        
        // Get last 10 viewed products (excluding current product)
        for (let i = Math.max(0, listSize - 10); i < listSize; i++) {
          const viewedProductId = data[`product_ID_${i}`];
          if (viewedProductId && viewedProductId !== productId) {
            productIds.push(viewedProductId);
          }
        }
        
        if (productIds.length > 0) {
          const products = [];
          for (const id of productIds.reverse()) {
            try {
              const productDoc = await getDoc(doc(firestore, 'PRODUCTS', id));
              if (productDoc.exists()) {
                products.push({ id: productDoc.id, ...productDoc.data() });
              }
            } catch (error) {
              console.error('Error loading recently viewed product:', error);
            }
          }
          setRecentlyViewed(products);
        }
      }
    } catch (error) {
      console.error('Error loading recently viewed:', error);
    }
  };

  const loadSearchBasedSuggestions = async () => {
    try {
      if (!currentUser) return;
      
      const searchHistoryRef = doc(firestore, 'USERS', currentUser.uid, 'USER_DATA', 'SEARCH_HISTORY');
      const searchHistoryDoc = await getDoc(searchHistoryRef);
      
      if (searchHistoryDoc.exists()) {
        const data = searchHistoryDoc.data();
        const listSize = data.list_size || 0;
        const searchTerms = [];
        
        // Get last 5 search terms
        for (let i = Math.max(0, listSize - 5); i < listSize; i++) {
          const searchTerm = data[`search_${i}`];
          if (searchTerm) {
            searchTerms.push(searchTerm.toLowerCase());
          }
        }
        
        if (searchTerms.length > 0) {
          const productsRef = collection(firestore, 'PRODUCTS');
          const products = [];
          
          // Search for products matching recent search terms
          for (const term of searchTerms) {
            const productsQuery = query(
              productsRef,
              where('product_title', '>=', term),
              where('product_title', '<=', term + '\uf8ff'),
              limit(5)
            );
            
            try {
              const snapshot = await getDocs(productsQuery);
              snapshot.docs.forEach(doc => {
                const productData = { id: doc.id, ...doc.data() };
                if (!products.find(p => p.id === productData.id) && productData.id !== productId) {
                  products.push(productData);
                }
              });
            } catch (error) {
              console.error('Error searching for products:', error);
            }
          }
          
          setSearchBasedSuggestions(products.slice(0, 12));
        }
      }
    } catch (error) {
      console.error('Error loading search-based suggestions:', error);
    }
  };

  const loadTrendingProducts = async () => {
    try {
      const productsRef = collection(firestore, 'PRODUCTS');
      const trendingQuery = query(
        productsRef,
        orderBy('total_ratings', 'desc'),
        limit(12)
      );
      
      const snapshot = await getDocs(trendingQuery);
      const products = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(p => {
          const pf = (p.productFor || 'user').toString().toLowerCase();
          if (!signedInRole) return pf === 'user';
          return signedInRole === 'retailer' ? pf === 'retailer' : pf === 'user';
        })
        .filter(p => p.id !== productId);
      
      setTrendingProducts(products);
    } catch (error) {
      console.error('Error loading trending products:', error);
    }
  };

  const loadCategoryProducts = async (category) => {
    try {
      if (!category) return;

      const productsRef = collection(firestore, 'PRODUCTS');
      // Remove orderBy to avoid index requirement - we'll sort in memory instead
      const categoryQuery = query(
        productsRef,
        where('category', '==', category),
        limit(20) // Increase limit since we're not ordering by rating
      );

      const snapshot = await getDocs(categoryQuery);
      let products = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(p => {
          const pf = (p.productFor || 'user').toString().toLowerCase();
          if (!signedInRole) return pf === 'user';
          return signedInRole === 'retailer' ? pf === 'retailer' : pf === 'user';
        })
        .filter(p => p.id !== productId)
        .sort((a, b) => (b.average_rating || 0) - (a.average_rating || 0)) // Sort by rating in memory
        .slice(0, 12); // Take top 12 after sorting

      setCategoryProducts(products);
    } catch (error) {
      console.error('Error loading category products:', error);
    }
  };

  const saveToRecentlyViewed = async () => {
    try {
      if (!currentUser || !productId) return;
      
      const recentlyViewedRef = doc(firestore, 'USERS', currentUser.uid, 'USER_DATA', 'RECENTLY_VIEWED');
      const recentlyViewedDoc = await getDoc(recentlyViewedRef);
      
      let updateData = {};
      let currentListSize = 0;
      
      if (recentlyViewedDoc.exists()) {
        const data = recentlyViewedDoc.data();
        currentListSize = data.list_size || 0;
        
        // Check if product already exists in recently viewed
        let existingIndex = -1;
        for (let i = 0; i < currentListSize; i++) {
          if (data[`product_ID_${i}`] === productId) {
            existingIndex = i;
            break;
          }
        }
        
        if (existingIndex === -1) {
          // Add new product
          updateData[`product_ID_${currentListSize}`] = productId;
          updateData[`timestamp_${currentListSize}`] = new Date().toISOString();
          updateData['list_size'] = currentListSize + 1;
        }
      } else {
        // First time viewing
        updateData[`product_ID_0`] = productId;
        updateData[`timestamp_0`] = new Date().toISOString();
        updateData['list_size'] = 1;
      }
      
      if (Object.keys(updateData).length > 0) {
        await setDoc(recentlyViewedRef, updateData, { merge: true });
      }
    } catch (error) {
      console.error('Error saving to recently viewed:', error);
    }
  };

  const navigateToProduct = (direction) => {
    if (relatedProducts.length === 0) return;
    
    let newIndex;
    if (direction === 'next') {
      newIndex = currentProductIndex < relatedProducts.length - 1 ? currentProductIndex + 1 : 0;
    } else {
      newIndex = currentProductIndex > 0 ? currentProductIndex - 1 : relatedProducts.length - 1;
    }
    
    const nextProduct = relatedProducts[newIndex];
    if (nextProduct) {
      history.push(`/product/${nextProduct.id}`);
    }
  };

  const handleAddToCart = async () => {
    if (!currentUser) {
      history.push('/signin');
      return;
    }

    setAddingToCart(true);
    try {
      if (isInCart) {
        // Remove from cart
        await dispatch(removeFromCart(productId));
        success('Product removed from cart!');
      } else {
        // Add to cart
        await dispatch(addToCart(productId, 1));
        success('Product added to cart!');
      }
    } catch (error) {
      showError(isInCart ? 'Failed to remove product from cart' : 'Failed to add product to cart');
    }
    setAddingToCart(false);
  };

  const handleBuyNow = () => {
    if (!currentUser) {
      history.push('/signin');
      return;
    }

    // Persist single-item checkout selection
    try {
      localStorage.setItem('checkoutItem', JSON.stringify({ productId, qty: 1 }));
    } catch (_) {}

    // Add to cart (to ensure availability) and redirect to shipping
    dispatch(addToCart(productId, 1));
    history.push('/shipping');
  };

  const loadUserRating = async () => {
    try {
      // Reset user rating first
      setUserRating(0);
      
      const userRatingsRef = doc(firestore, 'USERS', currentUser.uid, 'USER_DATA', 'MY_RATINGS');
      const userRatingsDoc = await getDoc(userRatingsRef);
      
      if (userRatingsDoc.exists()) {
        const ratingsData = userRatingsDoc.data();
        const listSize = ratingsData.list_size || 0;
        
        // Check if user has rated this product
        for (let i = 0; i < listSize; i++) {
          if (ratingsData[`product_ID_${i}`] === productId) {
            const rating = ratingsData[`rating_${i}`];
            if (rating && rating > 0) {
              setUserRating(rating);
            }
            break;
          }
        }
      }
    } catch (error) {
      console.error('Error loading user rating:', error);
      setUserRating(0); // Ensure it's reset on error
    }
  };

  const handleRating = async (rating) => {
    if (!currentUser) {
      history.push('/signin');
      return;
    }

    try {
      // Check if user has already rated this product
      const userRatingsRef = doc(firestore, 'USERS', currentUser.uid, 'USER_DATA', 'MY_RATINGS');
      const userRatingsDoc = await getDoc(userRatingsRef);
      
      let isUpdate = false;
      let previousRating = 0;
      let userRatingIndex = -1;
      
      if (userRatingsDoc.exists()) {
        const ratingsData = userRatingsDoc.data();
        const listSize = ratingsData.list_size || 0;
        
        // Check if product is already rated
        for (let i = 0; i < listSize; i++) {
          if (ratingsData[`product_ID_${i}`] === productId) {
            isUpdate = true;
            previousRating = ratingsData[`rating_${i}`];
            userRatingIndex = i;
            break;
          }
        }
      }

      // Update product rating
      const productRef = doc(firestore, 'PRODUCTS', productId);
      const updateData = {};
      
      if (isUpdate) {
        // Update existing rating
        if (rating !== previousRating) {
          updateData[`${previousRating}_star`] = increment(-1);
          updateData[`${rating}_star`] = increment(1);
          
          // Recalculate average rating
          const currentTotal = product.total_ratings || 0;
          const currentAverage = parseFloat(product.average_rating) || 0;
          const totalStars = (currentAverage * currentTotal) - previousRating + rating;
          const newAverage = totalStars / currentTotal;
          updateData['average_rating'] = parseFloat(newAverage.toFixed(1));
        }
      } else {
        // First time rating
        updateData[`${rating}_star`] = increment(1);
        updateData['total_ratings'] = increment(1);
        
        // Calculate new average rating
        const currentTotal = product.total_ratings || 0;
        const currentAverage = parseFloat(product.average_rating) || 0;
        const newAverage = ((currentAverage * currentTotal) + rating) / (currentTotal + 1);
        updateData['average_rating'] = parseFloat(newAverage.toFixed(1));
      }

      // Update product document
      await updateDoc(productRef, updateData);
      
      // Update user's rating history
      const userUpdateData = {};
      if (isUpdate) {
        userUpdateData[`rating_${userRatingIndex}`] = rating;
      } else {
        const currentListSize = userRatingsDoc.exists() ? (userRatingsDoc.data().list_size || 0) : 0;
        userUpdateData['list_size'] = currentListSize + 1;
        userUpdateData[`product_ID_${currentListSize}`] = productId;
        userUpdateData[`rating_${currentListSize}`] = rating;
      }
      
      await updateDoc(userRatingsRef, userUpdateData);
      
      // Update local state
      if (isUpdate) {
        if (rating !== previousRating) {
          setProduct(prev => ({
            ...prev,
            average_rating: updateData['average_rating'],
            [`${previousRating}_star`]: (prev[`${previousRating}_star`] || 0) - 1,
            [`${rating}_star`]: (prev[`${rating}_star`] || 0) + 1
          }));
        }
      } else {
        setProduct(prev => ({
          ...prev,
          total_ratings: (prev.total_ratings || 0) + 1,
          average_rating: updateData['average_rating'],
          [`${rating}_star`]: (prev[`${rating}_star`] || 0) + 1
        }));
      }
      
      setUserRating(rating);
      // Removed alert - using animated feedback in ProductRating component
      
    } catch (error) {
      console.error('Error submitting rating:', error);
      showError('Failed to submit rating');
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <div className="loading-text">Loading Product Details</div>
        <div className="loading-subtext">Please wait while we fetch the latest information...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <h2>Error</h2>
        <p>{error}</p>
        <button onClick={() => history.goBack()}>Go Back</button>
      </div>
    );
  }

  if (!product) {
    return <div>Product not found</div>;
  }

  // Reusable suggestion section component
  const SuggestionSection = ({ title, products, sectionType, gridRef }) => {
    if (!products || products.length === 0) return null;

    const scrollState = scrollStates[sectionType] || { canScrollLeft: false, canScrollRight: true };
    
    // Show arrows if there are enough products to scroll
    const hasScrollableContent = products.length > 3;

    return (
      <div className="product-suggestions">
        <div className="suggestions-inner">
          <h2 className="suggestions-title">{title}</h2>
          {hasScrollableContent && (
            <>
              <button
                type="button"
                className="suggestions-nav left"
                aria-label="Scroll left"
                onClick={() => scrollSuggestions('left', sectionType)}
                style={{ display: scrollState.canScrollLeft ? 'flex' : 'none' }}
              >
                <i className="fas fa-chevron-left"></i>
              </button>
              <button
                type="button"
                className="suggestions-nav right"
                aria-label="Scroll right"
                onClick={() => scrollSuggestions('right', sectionType)}
                style={{ display: 'flex' }} // Always show right arrow initially
              >
                <i className="fas fa-chevron-right"></i>
              </button>
            </>
          )}
          <div className="suggestions-grid" ref={gridRef}>
            {products.slice(0, 12).map(p => (
              <Product key={p.id} product={p} />
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="product-screen">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-container">
        <div className="breadcrumb">
          <span onClick={() => history.push('/')}>Home</span>
          <i className="fas fa-chevron-right"></i>
          <span onClick={() => history.push('/')}>{product.category || 'Products'}</span>
          <i className="fas fa-chevron-right"></i>
          <span className="current">{product.product_title}</span>
        </div>
      </div>

      {/* Separate containers for proper sticky behavior */}
      <div className="product-layout">
        {/* Sticky Images Container - Independent */}
        <div className="product-images-container" ref={imagesContainerRef}>
          <div className="product-images-section">
            <ProductImageGallery key={productId} images={productImages} productTitle={product.product_title} />
          </div>
        </div>

        {/* Scrollable Content Container - Independent */}
        <div className="product-content-container" ref={contentContainerRef}>
          <div className="product-content-wrapper">
            {/* Navigation Buttons */}
            {relatedProducts.length > 1 && (
              <div className="product-navigation">
                <button 
                  className="nav-btn prev-btn"
                  onClick={() => navigateToProduct('prev')}
                  title="Previous Product"
                >
                  <i className="fas fa-chevron-left"></i>
                </button>
                <button 
                  className="nav-btn next-btn"
                  onClick={() => navigateToProduct('next')}
                  title="Next Product"
                >
                  <i className="fas fa-chevron-right"></i>
                </button>
              </div>
            )}

            {/* Product Details Section */}
            <div className="product-details-section">
            <h1 className="product-title">{product.product_title}</h1>
            
            {/* Rating */}
            <div className="product-rating-mini">
              <span className="rating-value">{product.average_rating || '0.0'}</span>
              <div className="stars-mini">
                {[1, 2, 3, 4, 5].map(star => {
                  const rating = parseFloat(product.average_rating) || 0;
                  const isFilled = star <= rating;
                  const isPartial = star - 0.5 <= rating && star > rating;
                  
                  return (
                    <span key={star} className="star-wrapper">
                      <i className="fas fa-star star-bg"></i>
                      {isPartial ? (
                        <i className="fas fa-star-half-alt star-fill"></i>
                      ) : isFilled ? (
                        <i className="fas fa-star star-fill"></i>
                      ) : null}
                    </span>
                  );
                })}
              </div>
              <span className="rating-count">({product.total_ratings || 0} ratings)</span>
            </div>

            {/* Price */}
            <div className="product-pricing">
              <span className="current-price">₹{product.product_price}/-</span>
              {product.cutted_price && product.cutted_price !== product.product_price && (
                <span className="original-price">₹{product.cutted_price}/-</span>
              )}
            </div>

            {/* COD Indicator */}
            {product.COD && (
              <div className="cod-indicator">
                <i className="fas fa-truck"></i>
                <span>Cash on Delivery Available</span>
              </div>
            )}

            {/* Free Coupons */}
            {product.free_coupons > 0 && (
              <div className="free-coupons">
                <h4>{product.free_coupons} {product.free_coupon_title}</h4>
                <p>{product.free_coupon_body}</p>
              </div>
            )}

            {/* Coupon Redemption */}
            {currentUser && (
              <div className="coupon-section">
                <button 
                  className="coupon-btn"
                  onClick={() => setShowCouponDialog(true)}
                >
                  <i className="fas fa-ticket-alt"></i>
                  Apply Coupon
                </button>
              </div>
            )}

            {/* Wishlist and Share Actions */}
            <div className="secondary-actions">
              <WishlistButton productId={productId} />
              <button 
                className="share-btn"
                onClick={() => setShowShareDialog(true)}
              >
                <i className="fas fa-share-alt"></i>
                Share
              </button>
            </div>

            {/* Action Buttons */}
            <div className="product-buttons">
              {inStock ? (
                <>
                  <button
                    className={`add-to-cart-btn ${isInCart ? 'in-cart' : ''}`}
                    onClick={handleAddToCart}
                    disabled={addingToCart}
                  >
                    <i className="fas fa-shopping-cart"></i>
                    {addingToCart ? 'Processing...' : isInCart ? 'Remove from Cart' : 'Add to Cart'}
                  </button>
                  <button 
                    className="buy-now-btn"
                    onClick={handleBuyNow}
                  >
                    Buy Now
                  </button>
                </>
              ) : (
                <button className="out-of-stock-btn" disabled>
                  Out of Stock
                </button>
              )}
            </div>

            {/* Product Specification Tabs and Rating - moved inside details card */}
            <ProductSpecificationTabs product={product} />
            <ProductRating 
              product={product}
              userRating={userRating}
              onRate={handleRating}
            />
            </div>
          </div>
        </div>
      </div>

      {/* Multiple Suggestion Sections */}
      
      {/* Recently Viewed Products */}
      {currentUser && (
        <SuggestionSection
          title="Recently Viewed"
          products={recentlyViewed}
          sectionType="recentlyViewed"
          gridRef={recentlyViewedRef}
        />
      )}

      {/* Search-Based Suggestions */}
      {currentUser && (
        <SuggestionSection
          title="Based on Your Searches"
          products={searchBasedSuggestions}
          sectionType="searchBased"
          gridRef={searchBasedRef}
        />
      )}

      {/* Category-Based Products */}
      {product.category && (
        <SuggestionSection
          title={`More in ${product.category}`}
          products={categoryProducts}
          sectionType="category"
          gridRef={categoryRef}
        />
      )}

      {/* Related Products */}
      {relatedProducts && relatedProducts.length > 0 && (
        <SuggestionSection
          title="You may also like"
          products={relatedProducts.filter(p => p.id !== productId)}
          sectionType="related"
          gridRef={suggestionsRef}
        />
      )}

      {/* Trending Products */}
      <SuggestionSection
        title="Trending Now"
        products={trendingProducts}
        sectionType="trending"
        gridRef={trendingRef}
      />

      {/* Dialogs */}
      {showCouponDialog && (
        <CouponDialog 
          isOpen={showCouponDialog}
          product={product}
          onClose={() => setShowCouponDialog(false)}
          onCouponApply={(couponData) => {
            console.log('Coupon applied:', couponData);
            setShowCouponDialog(false);
          }}
        />
      )}

      {showShareDialog && (
        <ShareDialog 
          isOpen={showShareDialog}
          product={product}
          onClose={() => setShowShareDialog(false)}
        />
      )}
    </div>
  );
};

export default ProductScreen;
